import { H1, Tabs } from '@dnb/eufemia';
import type { ScopeFromAllSourcesDto } from '@portals/shared/admin/ScopeDto';
import type { ScopeRequestWithApiAndOwnerDto } from '@portals/shared/admin/ScopeRequestDto';
import { type JSX, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { backendClient } from '@/api/backendClient';
import BackButton from '@/components/BackButton';
import type { PagedResponseScopeAuditLogResponse } from '@/openapi/schema';

import ScopeAuditLogsTable from './components/ScopeAuditLogsTable';
import ScopeRequestsTable from './components/ScopeRequestsTable';
import ScopesTable from './components/ScopesTable';

const Redirect = ({ to }: { to: string }) => {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace: true }), [navigate, to]);
  return null;
};

enum TabKey {
  pending = 'pending',
  history = 'history',
  active = 'active',
}

const TABS = [
  { title: 'Scope Request', key: TabKey.pending },
  { title: 'Scope history', key: TabKey.history },
  { title: 'Scope overview', key: TabKey.active },
];

export default function ScopesList(): JSX.Element {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { data: scopes, isValidating: isScopeRequestLoading } =
    useSWR<ScopeRequestWithApiAndOwnerDto[]>('/scope-requests');
  const { data: activeScopes, isValidating: isActiveScopeLoading } = useSWR<ScopeFromAllSourcesDto[]>('/scopes');

  const currentTabKey = pathname.split('/')[2];

  const auditLogPage = Math.max(Number(searchParams.get('page')) || 0, 0);

  const { data: scopeAuditLogResponse, isValidating: isAuditLogLoading } =
    useSWR<PagedResponseScopeAuditLogResponse | null>(
      `v2:/scope-audit-logs?page=${auditLogPage}`,
      async () => {
        try {
          const { data } = await backendClient.GET('/scope-audit-logs', {
            params: { query: { page: auditLogPage } },
          });
          return data ?? null;
        } catch {
          return null;
        }
      },
      { keepPreviousData: true },
    );

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      prev.set('page', String(page));
      return prev;
    });
  };

  return (
    <>
      <BackButton to="/">Home</BackButton>
      <H1 top="large">Scopes {currentTabKey}</H1>
      <Tabs
        bottom="large"
        data={TABS}
        on_change={({ key }) => navigate(`/scopes/${key}`)}
        selected_key={pathname.split('/')[2]}
      />

      <Routes>
        <Route element={<Redirect to="pending" />} index />
        <Route
          element={<ScopeRequestsTable loading={isScopeRequestLoading} scopeRequests={scopes || []} />}
          path="/pending"
        />
        <Route
          element={
            <ScopeAuditLogsTable
              currentPage={auditLogPage}
              loading={isAuditLogLoading}
              onPageChange={handlePageChange}
              scopeAuditLogs={scopeAuditLogResponse?.content ?? null}
              totalElements={scopeAuditLogResponse?.totalElements ?? 0}
              totalPages={scopeAuditLogResponse?.totalPages ?? 0}
            />
          }
          path="/history"
        />
        <Route element={<ScopesTable loading={isActiveScopeLoading} scopes={activeScopes || []} />} path="/active" />
      </Routes>
    </>
  );
}
